import { SHOPIFY_COHORT_ANALYSIS_FAQ_IT } from '../shopifyCohortAnalysisFaqLd';

function faqToMarkdown(
  heading: string,
  rows: readonly { readonly q: string; readonly a: string }[],
): string {
  const chunks: string[] = [`## ${heading}`, ''];
  for (const { q, a } of rows) {
    chunks.push(`### ${q}`, '', a, '', '');
  }
  return chunks.join('\n').trimEnd();
}

const FAQ_IT_BODY = faqToMarkdown('FAQ', SHOPIFY_COHORT_ANALYSIS_FAQ_IT);

/**
 * Long-form article (IT) — analisi cohort Shopify e prodotti del primo acquisto.
 * Adattamento operativo (non traduzione letterale dell’EN).
 */
export const SHOPIFY_COHORT_ANALISI_PRODOTTI_RIACQUISTO_IT_BODY =
  `Molti ecommerce Shopify ottimizzano le campagne partendo da una domanda troppo limitata:

> “Quale prodotto ha il ROAS più alto sul primo ordine?”

È una domanda utile, ma non basta.

La domanda più importante è un’altra:

> “Quale prodotto porta clienti che tornano, comprano di nuovo e generano profitto nel tempo?”

Qui entra in gioco l’analisi cohort.

Non come semplice report di retention, ma come strumento operativo per decidere quali prodotti spingere in advertising, quali usare nei bundle, quali inserire nelle automazioni email e quali scalare senza bruciare margine.

Un prodotto può sembrare eccellente dentro Meta Ads o Google Ads perché converte bene al primo ordine. Ma se i clienti acquisiti con quel prodotto non tornano più, l’economia reale può essere fragile.

Al contrario, un prodotto può avere un ROAS iniziale più basso, ma generare clienti con maggiore LTV, maggiore probabilità di riacquisto e migliore payback del CAC.

---

## Cos’è l’analisi cohort su Shopify?

L’analisi cohort raggruppa i clienti in base a un punto di partenza comune.

Nel caso più frequente, il punto di partenza è il periodo in cui il cliente ha effettuato il primo acquisto.

Per esempio:

* clienti che hanno fatto il primo ordine a gennaio;
* clienti che hanno fatto il primo ordine a febbraio;
* clienti che hanno fatto il primo ordine a marzo.

Dopo aver creato queste coorti, puoi analizzare come si comportano nel tempo: ordini successivi, importo speso, retention, frequenza di riacquisto e valore generato.

Shopify include già un report nativo di [analisi cohort clienti](https://help.shopify.com/it/manual/reports-and-analytics/shopify-reports/report-types/default-reports/customers-reports). È una buona base per osservare il comportamento dei clienti dopo il primo acquisto.

Ma per prendere decisioni di crescita profittevole serve una domanda più precisa:

> Quali prodotti, quando vengono acquistati nel primo ordine, generano i clienti migliori?

Perché non tutti i primi ordini hanno lo stesso valore.

---

## Il punto critico: non tutti i prodotti acquisiscono lo stesso tipo di cliente

Due prodotti possono generare lo stesso fatturato oggi, ma clienti completamente diversi domani.

Esempio:

| Prodotto | ROAS primo ordine | Tasso di riacquisto | Net LTV |
| --- | ---: | ---: | ---: |
| Prodotto A | 3,2x | 8% | €58 |
| Prodotto B | 2,4x | 28% | €124 |

Se guardi solo il ROAS della piattaforma pubblicitaria, il Prodotto A sembra migliore.

Ma se il Prodotto B genera più clienti di ritorno e un LTV netto più alto, potrebbe essere il vero prodotto da scalare.

Questa è la differenza tra:

* ottimizzare le transazioni;
* ottimizzare i clienti;
* ottimizzare clienti profittevoli.

Per un ecommerce Shopify, questa distinzione impatta advertising, email marketing, merchandising, bundle, retention e gestione del magazzino.

---

## Le metriche da calcolare

Per capire quali prodotti generano riacquisti, serve un framework di metriche coerente.

| Metrica | Definizione | Perché conta |
| --- | --- | --- |
| Clienti primo acquisto | Clienti il cui primo ordine includeva uno specifico prodotto | Definisce la cohort di acquisizione |
| Clienti di ritorno | Clienti della cohort che hanno fatto almeno un ordine successivo | Misura la capacità del prodotto di generare riacquisto |
| Tasso di riacquisto | Clienti di ritorno / clienti primo acquisto | Indica la qualità retention del prodotto |
| Giorni mediani al secondo ordine | Giorni mediani tra primo e secondo ordine | Aiuta a temporizzare email, SMS e remarketing |
| Gross LTV | Ricavi complessivi generati dalla cohort | Utile, ma incompleto |
| Net LTV | Ricavi al netto di sconti, resi, COGS e costi variabili | Più vicino al valore economico reale |
| CAC payback | Tempo necessario per recuperare il costo di acquisizione | Fondamentale per scalare l’advertising |
| Copertura magazzino | Stock attuale / velocità di vendita | Evita di scalare prodotti destinati a finire subito |

Il punto è non guardare la retention da sola.

Un alto tasso di riacquisto è utile solo se gli ordini successivi sono profittevoli, il magazzino è sufficiente e il costo di acquisizione è sostenibile.

---

## Come impostare l’analisi

Il modo più solido per fare questa analisi è partire dai dati ordine, cliente e line item.

### 1. Identifica il primo ordine di ogni cliente

Per ogni cliente devi individuare il primo ordine.

Ti servono almeno:

* ID cliente o email;
* ID ordine;
* data ordine;
* ricavi ordine;
* sconti;
* resi o rimborsi;
* prodotti acquistati;
* SKU o product ID;
* costo prodotto, se disponibile.

L’obiettivo è isolare il primo ordine di ciascun cliente.

---

### 2. Associa i prodotti del primo acquisto

Una volta identificato il primo ordine, devi vedere quali prodotti erano presenti in quell’ordine.

Se il primo ordine contiene un solo prodotto, l’attribuzione è semplice.

Se contiene più prodotti, devi scegliere una regola.

Possibili approcci:

* associare il cliente a tutti i prodotti presenti nel primo ordine;
* associare il cliente al prodotto di maggior valore;
* associare il cliente allo SKU principale;
* trattare separatamente bundle e prodotti singoli.

Per molti ecommerce Shopify, la soluzione più pratica è associare il cliente a tutti i prodotti presenti nel primo ordine.

In questo modo rispondi a una domanda molto concreta:

> “Quando questo prodotto compare nel primo ordine, con quale probabilità quel cliente compra di nuovo?”

---

### 3. Calcola i clienti di ritorno

Un cliente è “di ritorno” se ha effettuato almeno un ordine successivo al primo.

La formula base è:

    Tasso di riacquisto = clienti di ritorno / clienti primo acquisto

Esempio:

    52 clienti hanno acquistato il Prodotto X nel primo ordine.
    4 di questi clienti hanno fatto almeno un ordine successivo.
    Tasso di riacquisto = 4 / 52 = 7,7%

Attenzione: questo non significa che il Prodotto X ha generato quattro ordini successivi.

Significa che quattro clienti, entrati attraverso un primo ordine che includeva il Prodotto X, sono diventati clienti di ritorno.

La differenza è importante.

---

### 4. Misura il tempo al secondo ordine

Il tasso di riacquisto da solo non basta.

Devi anche capire dopo quanto tempo il cliente compra di nuovo.

Se il secondo ordine mediano arriva dopo 18 giorni, la strategia email sarà diversa rispetto a un prodotto in cui il secondo ordine arriva dopo 90 giorni.

Metriche utili:

* giorni mediani al secondo ordine;
* giorni medi al secondo ordine;
* repeat rate a 30, 60, 90 e 180 giorni;
* LTV cumulato per mese successivo al primo acquisto.

Così l’analisi retention diventa uno strumento operativo per lifecycle marketing, non solo un report descrittivo.

---

### 5. Aggiungi margini, sconti e resi

Il fatturato lordo può ingannare.

Un prodotto può generare clienti di ritorno, ma avere un’economia debole se:

* gli sconti sul primo ordine sono troppo aggressivi;
* i resi sono elevati;
* il COGS è alto;
* la spedizione assorbe troppo margine;
* gli ordini successivi hanno marginalità bassa.

Per questo il Net LTV è più utile del Gross LTV.

Formula semplificata:

    Net LTV = ricavi netti - sconti - resi - costo prodotto - costi variabili di evasione

Non serve partire con un modello finanziario perfetto.

Anche una vista direzionale sul margine di contribuzione è molto più utile del solo fatturato.

---

### 6. Collega i dati advertising

Il vero salto avviene quando colleghi le cohort di primo acquisto ai dati di acquisizione.

Esempio:

| Prodotto primo acquisto | CAC | ROAS primo ordine | Tasso riacquisto | Net LTV | CAC payback |
| --- | --: | ---: | ---: | ---: | --- |
| Prodotto A | €31 | 3,2x | 8% | €58 | Debole |
| Prodotto B | €38 | 2,4x | 28% | €124 | Forte |
| Prodotto C | €22 | 4,1x | 3% | €41 | Scarso |

Qui il ROAS della piattaforma non basta più.

Meta Ads e Google Ads ti dicono cosa è successo dentro le loro finestre di attribuzione. Ma di solito non ti dicono se i clienti acquisiti tramite un certo prodotto diventano profittevoli nel tempo.

Per capirlo devi collegare dati Shopify, ordini, prodotti, clienti, margini e spesa advertising. Per un collegamento con profitto e canali, vedi anche [Meta Ads MCP per Shopify](/it/blog/meta-ads-mcp-per-shopify).

---

## Come usare questi insight

Quando sai quali prodotti generano clienti di ritorno, puoi prendere decisioni migliori su tutta la macchina ecommerce.

### Advertising

Non scalare solo il prodotto con il ROAS più alto sul primo ordine.

Scala i prodotti che hanno:

* CAC sostenibile;
* buon tasso di riacquisto;
* Net LTV interessante;
* margine sano;
* sufficiente copertura magazzino.

Questo è particolarmente importante per categorie come beauty, fashion, food, pet, integratori, prodotti consumabili o ecommerce con alta probabilità di repeat purchase.

---

### Email e SMS marketing

Il prodotto acquistato nel primo ordine dovrebbe influenzare le automazioni post-acquisto.

Un cliente che compra un prodotto riacquistabile potrebbe ricevere:

* reminder di riordino;
* proposta subscription;
* suggerimenti di bundle;
* comunicazioni basate sul ciclo di consumo.

Un cliente che compra un prodotto entry-level potrebbe ricevere:

* contenuti educativi;
* sequenze di cross-sell;
* offerte per il secondo acquisto;
* suggerimenti verso prodotti a margine più alto.

Il primo prodotto è un segnale. Va usato.

---

### Merchandising e bundle

L’analisi cohort può mostrare quali prodotti non sono solo best seller, ma veri “customer starter”.

Questa informazione può guidare:

* homepage;
* landing page;
* bundle;
* raccomandazioni prodotto;
* offerte post-purchase;
* gift-with-purchase;
* strategie di cross-sell.

Un prodotto con margine più basso sul primo ordine può comunque essere strategico se genera un percorso cliente molto più forte.

---

### Magazzino

La retention conta solo se puoi soddisfare la domanda.

Prima di scalare un prodotto che genera clienti di ritorno, devi controllare:

* stock disponibile;
* copertura magazzino;
* sell-through rate;
* tempi di riordino;
* lead time del fornitore;
* disponibilità a livello variante.

Shopify offre [report inventory](https://help.shopify.com/it/manual/reports-and-analytics/shopify-reports/report-types/default-reports/inventory-reports) e [analytics prodotto](https://help.shopify.com/it/manual/products/analytics) utili su venduto, sell-through e giorni di stock. Il valore emerge quando colleghi questi numeri a LTV, margine e advertising.

Un prodotto che crea ottimi clienti ma va sempre out-of-stock limita la crescita e peggiora l’esperienza cliente.

---

## Errori comuni nell’analisi cohort Shopify

### Errore 1: guardare solo il fatturato

Il fatturato non è valore cliente.

Quando possibile, passa da revenue a margine di contribuzione o Net LTV.

---

### Errore 2: ignorare la dimensione del campione

Se un prodotto ha solo 12 clienti di primo acquisto, il tasso di riacquisto può essere interessante, ma poco robusto.

Serve una soglia minima prima di prendere decisioni aggressive sul budget.

Regola pratica:

* sotto 50 clienti di primo acquisto: segnale direzionale;
* sopra 100 clienti: segnale più utilizzabile, a seconda del volume e della categoria.

---

### Errore 3: confondere prodotto, variante e bundle

L’analisi a livello prodotto può nascondere differenze importanti.

Una variante nera, una limited edition e un bundle possono avere comportamenti completamente diversi.

Decidi se analizzare a livello:

* prodotto;
* variante;
* SKU;
* bundle;
* categoria.

---

### Errore 4: usare finestre temporali sbagliate

Un prodotto con ciclo di riacquisto a 12 mesi sembrerà debole se analizzi solo 30 giorni.

La finestra temporale deve essere coerente con la categoria.

Esempi:

* beauty: 30–90 giorni;
* fashion: 60–180 giorni;
* integratori: 30–60 giorni;
* arredamento: molto più lungo;
* pet food: spesso altamente riacquistabile.

---

### Errore 5: considerare il ROAS come KPI finale

Il ROAS è utile, ma non è il business model.

Spesso non include correttamente:

* costo di acquisizione blended;
* contributo organico;
* comportamento dei clienti di ritorno;
* resi;
* sconti;
* margine;
* stock;
* incrementalità.

Per un ecommerce, il ROAS deve essere collegato all’economia cliente, non trattato come metrica definitiva.

---

## Report Shopify nativi vs profit analytics

I report nativi Shopify sono un ottimo punto di partenza.

Ti aiutano a osservare retention, cohort, vendite, clienti e inventory.

Ma le decisioni di crescita richiedono quasi sempre un modello più ampio.

Per decidere cosa scalare devi collegare:

* ordini Shopify;
* customer cohort;
* prodotti del primo acquisto;
* spesa Meta Ads;
* spesa Google Ads;
* dati GA4;
* sconti;
* resi;
* COGS;
* margini;
* copertura magazzino.

L’obiettivo non è sostituire Shopify.

L’obiettivo è collegare Shopify al resto del contesto operativo ed economico dell’ecommerce.

---

## Come aiuta Kipify

Kipify aiuta i team ecommerce a collegare dati Shopify, advertising e analytics in un’unica vista.

Invece di passare continuamente da Shopify, Meta Ads, Google Ads, GA4 e fogli di calcolo, puoi analizzare con maggiore chiarezza:

* blended ROAS;
* MER;
* performance prodotto;
* KPI custom;
* marginalità;
* inventory health;
* retention;
* repeat purchase behavior.

Nell’analisi cohort, il vero valore non è solo sapere che un cliente è tornato.

Il valore è sapere:

> quale prodotto ha acquisito quel cliente, quanto è costato acquisirlo, se ha comprato di nuovo, quanto profitto ha generato e se puoi scalare quel prodotto senza creare problemi di magazzino.

Questa è la visibilità che serve per crescere in modo profittevole.

---

## Checklist operativa

Prima di scalare un prodotto in advertising, rispondi a queste domande:

* Questo prodotto genera clienti di ritorno?
* Qual è il tasso di riacquisto per prodotto del primo ordine?
* Dopo quanti giorni i clienti fanno il secondo ordine?
* Qual è il Net LTV, non solo il fatturato lordo?
* Qual è il CAC payback?
* Sconti e resi stanno comprimendo il margine?
* C’è abbastanza stock per scalare?
* Gli ordini successivi sono profittevoli?
* Questo prodotto è solo un best seller o è davvero un customer starter?

Se non sai rispondere, probabilmente stai scalando con dati incompleti.

---

` +
  FAQ_IT_BODY +
  `

---

## Conclusione

Il prodotto più venduto non è sempre il miglior prodotto da usare per acquisire clienti.

E il prodotto con il ROAS più alto sul primo ordine non è sempre quello che genera più profitto nel tempo.

L’analisi cohort diventa davvero utile quando colleghi il primo prodotto acquistato a retention, LTV, CAC, margini e magazzino.

È così che passi dal semplice reporting alla decisione strategica su cosa scalare.

---

## Prossimo passo

**Vuoi capire quali prodotti generano davvero clienti di ritorno?** [Richiedi un audit gratuito di analytics ecommerce](/landing-adv/it) — Kipify collega Shopify, Meta Ads, Google Ads e GA4 per aiutarti ad analizzare retention, LTV, margini e inventory in un’unica vista.
`;
